import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, ExternalLink, Heart, Package as PackageIcon, ShoppingBag, X, AlertTriangle, TrendingDown } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { dealsAPI } from '../lib/api';
import { trackEvent } from '../lib/api';
import ShareButton from '../components/ShareButton';

function ProductHero({ url, title }) {
  const [broken, setBroken] = React.useState(false);
  React.useEffect(() => { setBroken(false); }, [url]);
  if (!url || broken) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center text-slate-300">
        <PackageIcon className="w-24 h-24" />
        <span className="text-xs text-slate-400 mt-2">Photo indisponible</span>
      </div>
    );
  }
  return <img src={url} alt={title} className="w-full h-full object-cover" onError={() => setBroken(true)} />;
}


export default function DealProductPage() {
  const { slug, productSlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [interestOpen, setInterestOpen] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const [merchantOpen, setMerchantOpen] = useState(false);
  const [reserving, setReserving] = useState(false);
  const isLogged = !!localStorage.getItem('token');

  const load = async () => {
    try {
      const { data } = await dealsAPI.getProduct(slug, productSlug);
      setData(data);
      // Fire funnel event: viewed a specific product page
      trackEvent('deal_page_view', {
        deal_slug: slug,
        product_slug: productSlug,
        price: (data?.dp?.current_price ?? data?.dp?.fixed_price ?? 0).toString(),
      });
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [slug, productSlug]);

  if (loading) {
    return <><Navbar /><div className="min-h-screen flex items-center justify-center pt-24"><Loader2 className="w-8 h-8 animate-spin text-amber-600" /></div></>;
  }
  if (!data) {
    return <><Navbar /><div className="min-h-screen flex items-center justify-center pt-24"><p className="text-slate-600">Produit introuvable</p></div></>;
  }
  const { deal, deal_product: dp } = data;
  const p = dp.product;

  const handleMerchantClick = () => {
    setMerchantOpen(false);
    dealsAPI.trackClick(deal.id, dp.id).catch(() => {});
    trackEvent('merchant_click', { deal_slug: slug, product_slug: productSlug });
    const url = dp.merchant_url_override || p?.merchant_url;
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleReserve = async () => {
    trackEvent('reserve_click', { deal_slug: slug, product_slug: productSlug, price: String(dp?.current_price || 0) });
    if (!isLogged) {
      toast.info('Connectez-vous pour réserver votre place');
      window.location.href = `/login?next=${encodeURIComponent(window.location.pathname)}`;
      return;
    }
    setReserving(true);
    try {
      const { data } = await dealsAPI.checkout(deal.id, dp.id, window.location.origin);
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossible de démarrer le paiement');
      setReserving(false);
    }
  };

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-slate-50 pt-20 pb-16">
        <section className="max-w-6xl mx-auto px-4 py-8">
          <Link to={`/deals/${slug}`} className="text-amber-700 hover:text-amber-800 text-sm mb-4 inline-block">← Retour au deal</Link>

          <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-200 relative">
            <div className="absolute top-4 right-4 z-10">
              <ShareButton
                variant="icon"
                title={`${p?.brand ? p.brand + ' — ' : ''}${p?.title}`}
                message={`🔥 Deal ISM Parcel — ${deal.destination?.name} : ${p?.brand ? p.brand + ' ' : ''}${p?.title} à ${p?.merchant_price?.toFixed(2)}€ + seulement ${dp.current_price?.toFixed(2)}€ de livraison groupée ! Plus on est nombreux, moins ça coûte. 👉 ${window.location.href}`}
                testId="product-share-btn"
              />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-6 lg:p-10">
              {/* PHOTO */}
              <div className="aspect-square rounded-xl overflow-hidden bg-slate-100">
                <ProductHero url={p?.photos?.[0]} title={p?.title} />
              </div>

              {/* INFO */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Badge className="bg-amber-500 hover:bg-amber-600">{deal.destination?.flag} {deal.destination?.name}</Badge>
                  {dp.featured && <Badge className="bg-slate-900">⭐ À la une</Badge>}
                </div>
                {p?.brand && <div className="text-sm text-slate-500 uppercase tracking-wider font-bold mb-1">{p.brand}</div>}
                <h1 className="text-2xl lg:text-3xl font-black text-slate-900 mb-3" style={{fontFamily: 'Chivo, sans-serif'}}>{p?.title}</h1>
                {p?.reference && <div className="text-xs text-slate-500 mb-4">Réf. {p.reference}</div>}
                {p?.description && <p className="text-slate-600 mb-6 leading-relaxed">{p.description}</p>}

                {/* PRICE BOX */}
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-5 mb-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-slate-500 uppercase font-bold">Prix marchand</div>
                      <div className="text-2xl font-black text-slate-900">{p?.merchant_price?.toFixed(2)} €</div>
                      <div className="text-xs text-slate-500">{p?.merchant_name || 'Marchand'}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-amber-700 uppercase font-bold">Livraison actuelle</div>
                      <div className="text-3xl font-black text-amber-700">{dp.current_price?.toFixed(2)} €</div>
                      <div className="text-xs text-amber-700">Tarif débloqué</div>
                    </div>
                  </div>

                  {dp.next_tier && (
                    <div className="mt-4 pt-4 border-t border-amber-200">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingDown className="w-4 h-4 text-green-700" />
                        <span className="text-sm font-semibold text-green-900">
                          Encore {dp.orders_to_next_tier} commande(s) pour passer à {dp.next_tier.price.toFixed(2)} €
                        </span>
                      </div>
                      <div className="w-full h-2 bg-white rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-amber-400 to-green-500 rounded-full transition-all"
                          style={{ width: `${Math.min(100, (dp.received_orders_count / (dp.next_tier.min_orders || 1)) * 100)}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* MINIMUM VIABILITY */}
                {dp.min_orders_to_ship > 0 && (
                  <div className={`rounded-xl p-4 mb-4 border-2 ${dp.is_viable ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-300'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{dp.is_viable ? '✅' : '⏳'}</span>
                        <span className="text-sm font-bold text-slate-900">
                          {dp.is_viable
                            ? 'Objectif minimum atteint !'
                            : `Objectif : ${dp.min_orders_to_ship} commandes minimum`}
                        </span>
                      </div>
                      <span className="text-xs font-semibold text-slate-600">
                        {dp.received_orders_count}/{dp.min_orders_to_ship}
                      </span>
                    </div>
                    <div className="w-full h-2.5 bg-white rounded-full overflow-hidden mb-2">
                      <div
                        className={`h-full rounded-full transition-all ${dp.is_viable ? 'bg-green-500' : 'bg-orange-400'}`}
                        style={{ width: `${dp.viability_progress_percent}%` }}
                      />
                    </div>
                    {!dp.is_viable && (
                      <p className="text-xs text-orange-800">
                        L&apos;expédition partira dès que <strong>{dp.orders_needed_for_viability} commande(s)</strong> supplémentaire(s) auront été reçues chez ISM Parcel.
                      </p>
                    )}
                  </div>
                )}

                {/* COUNTERS */}
                <div className="grid grid-cols-3 gap-2 mb-6">
                  <div className="bg-slate-50 rounded-lg px-3 py-2 text-center">
                    <div className="text-xl font-black text-slate-900">{dp.interested_count || 0}</div>
                    <div className="text-xs text-slate-500">👀 Intéressés</div>
                  </div>
                  <div className="bg-amber-50 rounded-lg px-3 py-2 text-center">
                    <div className="text-xl font-black text-amber-900">{dp.declared_orders_count || 0}</div>
                    <div className="text-xs text-amber-700">🛒 Déclarées</div>
                  </div>
                  <div className="bg-green-50 rounded-lg px-3 py-2 text-center">
                    <div className="text-xl font-black text-green-900">{dp.received_orders_count || 0}</div>
                    <div className="text-xs text-green-700">📦 Reçues</div>
                  </div>
                </div>

                {/* CTAs */}
                <Button
                  className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-black text-base py-6 shadow-lg mb-2"
                  onClick={handleReserve}
                  disabled={reserving}
                  data-testid="btn-reserve-pay"
                >
                  {reserving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <PackageIcon className="w-5 h-5 mr-2" />}
                  Réserver ma place — Payer {dp.current_price?.toFixed(2)} €
                </Button>
                {!isLogged && (
                  <p className="text-xs text-slate-500 text-center mb-2 italic">
                    Vous devez être <Link to="/login" className="underline text-slate-700">connecté</Link> pour réserver
                  </p>
                )}
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setMerchantOpen(true)}
                    data-testid="btn-merchant"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" /> Voir chez le marchand
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => { trackEvent('interest_click', { deal_slug: slug, product_slug: productSlug }); setInterestOpen(true); }}
                    data-testid="btn-interested"
                  >
                    <Heart className="w-4 h-4 mr-2" /> Juste intéressé
                  </Button>
                </div>

                <div className="mt-6 space-y-2 text-xs text-slate-500 bg-slate-50 rounded-lg p-4">
                  <p><strong>Produit vendu par {p?.merchant_name || 'le marchand indiqué'}.</strong> ISM Parcel intervient uniquement dans la réception, le regroupement et l&apos;acheminement de votre commande.</p>
                  <p>Le prix marchand est indicatif et peut évoluer sur le site du vendeur.</p>
                  {(p?.est_length_cm || p?.est_weight_kg) && (
                    <p>Dimensions estimées : {p.est_length_cm || '?'}×{p.est_width_cm || '?'}×{p.est_height_cm || '?'} cm — {p.est_weight_kg || '?'} kg</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {/* MODALS */}
      {interestOpen && (
        <InterestModal deal={deal} dp={dp} onClose={() => setInterestOpen(false)} onSuccess={() => { setInterestOpen(false); load(); }} />
      )}
      {orderOpen && (
        <OrderModal deal={deal} dp={dp} onClose={() => setOrderOpen(false)} onSuccess={() => { setOrderOpen(false); load(); }} />
      )}
      {merchantOpen && (
        <MerchantModal onClose={() => setMerchantOpen(false)} onConfirm={handleMerchantClick} customerNumber={p?.merchant_name} />
      )}
    </>
  );
}

function InterestModal({ deal, dp, onClose, onSuccess }) {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', customer_number: '' });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email) { toast.error('Prénom et e-mail requis'); return; }
    setLoading(true);
    try {
      const { data } = await dealsAPI.expressInterest(deal.id, dp.id, form);
      if (data.already_interested) toast.info('Vous êtes déjà inscrit(e) pour ce produit !');
      else toast.success('Votre intérêt a bien été enregistré. Merci !');
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
    setLoading(false);
  };
  return (
    <Modal onClose={onClose} title="Je suis intéressé" testId="modal-interest">
      <p className="text-sm text-slate-600 mb-4">Enregistrez votre intérêt pour ce produit. Vous serez comptabilisé mais rien ne vous engage.</p>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div><Label>Prénom *</Label><Input value={form.first_name} onChange={e=>setForm({...form, first_name:e.target.value})} data-testid="interest-firstname" /></div>
          <div><Label>Nom</Label><Input value={form.last_name} onChange={e=>setForm({...form, last_name:e.target.value})} data-testid="interest-lastname" /></div>
        </div>
        <div><Label>E-mail *</Label><Input type="email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} data-testid="interest-email" /></div>
        <div><Label>Code client ISM (facultatif)</Label><Input value={form.customer_number} onChange={e=>setForm({...form, customer_number:e.target.value})} placeholder="CB-XXXXXX" data-testid="interest-customer" /></div>
        <Button type="submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="interest-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Valider mon intérêt'}
        </Button>
      </form>
    </Modal>
  );
}

function OrderModal({ deal, dp, onClose, onSuccess }) {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', customer_number: '', merchant_order_number: '', tracking_number: '', order_date: '' });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.merchant_order_number) { toast.error('Champs obligatoires manquants'); return; }
    setLoading(true);
    try {
      await dealsAPI.declareOrder(deal.id, dp.id, form);
      toast.success('Commande déclarée. Nous attendons sa réception chez ISM Parcel.');
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
    setLoading(false);
  };
  return (
    <Modal onClose={onClose} title="J'ai commandé chez le marchand" testId="modal-declare">
      <p className="text-sm text-slate-600 mb-4">Déclarez votre achat pour que nous puissions relier votre colis à ce Deal dès sa réception.</p>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div><Label>Prénom *</Label><Input value={form.first_name} onChange={e=>setForm({...form, first_name:e.target.value})} data-testid="declare-firstname" /></div>
          <div><Label>Nom *</Label><Input value={form.last_name} onChange={e=>setForm({...form, last_name:e.target.value})} data-testid="declare-lastname" /></div>
        </div>
        <div><Label>E-mail *</Label><Input type="email" value={form.email} onChange={e=>setForm({...form, email:e.target.value})} data-testid="declare-email" /></div>
        <div><Label>Code client ISM</Label><Input value={form.customer_number} onChange={e=>setForm({...form, customer_number:e.target.value})} placeholder="CB-XXXXXX" data-testid="declare-customer" /></div>
        <div><Label>N° commande marchand *</Label><Input value={form.merchant_order_number} onChange={e=>setForm({...form, merchant_order_number:e.target.value})} data-testid="declare-order-num" /></div>
        <div><Label>N° de suivi (si dispo)</Label><Input value={form.tracking_number} onChange={e=>setForm({...form, tracking_number:e.target.value})} data-testid="declare-tracking" /></div>
        <div><Label>Date de commande</Label><Input type="date" value={form.order_date} onChange={e=>setForm({...form, order_date:e.target.value})} data-testid="declare-date" /></div>
        <Button type="submit" disabled={loading} className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold" data-testid="declare-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Déclarer ma commande'}
        </Button>
      </form>
    </Modal>
  );
}

function MerchantModal({ onClose, onConfirm }) {
  return (
    <Modal onClose={onClose} title="Avant de commander" testId="modal-merchant">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 flex gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900">
          <p className="font-semibold mb-1">Assurez-vous de :</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Commander exactement la <strong>référence</strong> présentée dans ce Deal</li>
            <li>Utiliser votre <strong>adresse ISM Parcel</strong></li>
            <li>Indiquer correctement votre <strong>code client</strong> (CB-XXXXXX)</li>
          </ul>
        </div>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" onClick={onClose} className="flex-1" data-testid="merchant-cancel">Annuler</Button>
        <Button onClick={onConfirm} className="flex-1 bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold" data-testid="merchant-continue">
          Continuer vers le marchand <ExternalLink className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </Modal>
  );
}

function Modal({ onClose, title, children, testId }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose} data-testid={testId}>
      <div className="bg-white rounded-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-xl font-bold text-slate-900" style={{fontFamily: 'Chivo, sans-serif'}}>{title}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid={`${testId}-close`}><X className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
